import React, { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import api, { formatMoney, formatMoney2 } from "../../lib/api";
import { TopeIncentivos } from "./TopeIncentivos";

const C = { individual: "#0284c7", grupal: "#8b5cf6", parado: "#f59e0b", beneficio: "#059669" };

function Dato({ label, value, tone, testid }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: tone }}>{label}</div>
      <div className="font-display text-lg font-extrabold">{value}</div>
    </div>
  );
}

export function CosteIncentivos() {
  const [d, setD] = useState(null);
  const load = () => api.get("/incentivos-coste").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d?.actual) return null;
  const a = d.actual;
  const pct = a.beneficio_extra > 0 ? (100 * a.total) / a.beneficio_extra : null;
  return (
    <div className="card p-5 mb-6" data-testid="coste-incentivos">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Wallet size={12} /> {a.label}</div>
          <h3 className="font-display text-lg font-bold">Coste total de los incentivos</h3>
          <p className="text-xs text-slate-500">Individual + grupal + stock parado, frente al beneficio extra que genera la subida de venta libre (cálculo del incentivo grupal).</p>
        </div>
        {pct != null && (
          <div className={"rounded-xl px-4 py-2 text-right " + (pct <= 100 ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700")} data-testid="coste-incentivos-pct">
            <div className="text-[10px] uppercase tracking-widest font-bold">Se reparte</div>
            <div className="font-display text-2xl font-extrabold">{pct.toFixed(0)} %</div>
            <div className="text-[11px]">del beneficio extra</div>
          </div>
        )}
      </div>
      <TopeIncentivos a={a} p={d.propuesta} onApplied={load} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <Dato label="Individual" value={formatMoney2(a.individual)} tone={C.individual} testid="coste-individual" />
        <Dato label="Grupal" value={formatMoney2(a.grupal)} tone={C.grupal} testid="coste-grupal" />
        <Dato label="Stock parado" value={formatMoney2(a.parado)} tone={C.parado} testid="coste-parado" />
        <Dato label="Total" value={formatMoney2(a.total)} tone="#0f172a" testid="coste-total" />
        <Dato label="Beneficio extra" value={a.beneficio_extra == null ? "—" : formatMoney2(a.beneficio_extra)} tone={C.beneficio} testid="coste-beneficio" />
      </div>
      {d.serie.length > 1 && (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={d.serie} data-testid="coste-incentivos-grafica">
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatMoney(v)} width={70} />
            <Tooltip formatter={(v) => formatMoney2(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="individual" stackId="c" name="Individual" fill={C.individual} />
            <Bar dataKey="grupal" stackId="c" name="Grupal" fill={C.grupal} />
            <Bar dataKey="parado" stackId="c" name="Stock parado" fill={C.parado} radius={[3, 3, 0, 0]} />
            <Line dataKey="beneficio_extra" name="Beneficio extra" stroke={C.beneficio} strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
